import React from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { clearSuperAdminToken, hasSuperAdminToken } from '../../utils/superAdminToken';
import { saRequest } from '../../utils/superAdminAxiosCaller';
import './styles/super-admin.scss';

interface Props {
    /** Email of the current super-admin, if known. Shown in top-right. */
    currentEmail?: string | null;
    children: React.ReactNode;
}

/**
 * Distinct shell for the "Mika Ops" panel. Indigo topbar, amber accents,
 * deliberately non-tenant chrome so you always know which auth tier you're in.
 * Red is reserved EXCLUSIVELY for the impersonation banner in a future phase.
 */
const SuperAdminLayout: React.FC<Props> = ({ currentEmail, children }) => {
    const history = useHistory();

    const handleLogout = async () => {
        if (hasSuperAdminToken()) {
            try {
                await saRequest('POST', 'auth/logout');
            } catch (e) {
                // Best-effort — clear locally even if the server call fails.
            }
        }
        clearSuperAdminToken();
        history.replace('/mika/ops/login');
    };

    const envLabel = (process.env.REACT_APP_ENV || process.env.NODE_ENV || '').toUpperCase();

    return (
        <div className="sa-shell">
            <header className="sa-topbar">
                <div className="sa-brand">
                    <span className="sa-brand-dot" aria-hidden="true" />
                    <span className="sa-brand-mark">Mika</span>
                    <span className="sa-brand-suffix">Ops</span>
                    {envLabel && envLabel !== 'PRODUCTION' && (
                        <span className="sa-env-badge">{envLabel}</span>
                    )}
                </div>

                <nav className="sa-nav" aria-label="Super admin navigation">
                    <NavLink exact to="/mika/ops" activeClassName="active">
                        Dashboard
                    </NavLink>
                    <NavLink to="/mika/ops/companies" activeClassName="active">
                        Companies
                    </NavLink>
                    <NavLink exact to="/mika/ops/known-ips" activeClassName="active">
                        Known IPs
                    </NavLink>
                    <NavLink exact to="/mika/ops/audit-log" activeClassName="active">
                        Audit log
                    </NavLink>
                </nav>

                <div className="sa-user">
                    {currentEmail && <span className="sa-user-email">{currentEmail}</span>}
                    <button type="button" onClick={handleLogout}>
                        Log out
                    </button>
                </div>
            </header>

            <main className="sa-content">{children}</main>
        </div>
    );
};

export default SuperAdminLayout;
